import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useShop } from "@/contexts/ShopContext";
import { ALL_PERMISSIONS, PERMISSION_LABELS } from "@/constants/permissions";
import Modal from "@/components/Modal";
import { toast } from "sonner";

export default function UserAccess() {
  const { user } = useAuth();
  const { shops } = useShop();
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", shop_id: "", permissions: [] });

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm({ name: "", email: "", password: "", role: user.role === "admin" ? "shop_admin" : "staff", shop_id: shops[0]?.id || "", permissions: [] });
    setModalOpen(true);
  };

  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) { toast.error("All fields are required"); return; }
    try {
      await api.post("/users", form);
      toast.success("User created");
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create user");
    }
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
    load();
  };

  return (
    <section className="view" data-testid="user-access-page">
      <div className="page-head">
        <div>
          <div className="eyebrow">People on the pass</div>
          <h1>User Access</h1>
          <p>Roles and station access for the team.</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd} data-testid="add-user-btn">＋ Add user profile</button>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="section-head"><div><h2>Roles &amp; access</h2><p>Each tier scoped to what it needs, nothing more</p></div></div>
        <div className="order-row"><span className="chip" style={{ flex: "none" }}>Admin</span><div className="order-main"><div className="order-meta">Manages shops within their tenant, creates Shop Admins and grants permissions.</div></div></div>
        <div className="order-row"><span className="chip" style={{ flex: "none" }}>Shop Admin</span><div className="order-main"><div className="order-meta">Full access, scoped to their one assigned shop. Creates User Profiles under that shop.</div></div></div>
        <div className="order-row"><span className="chip" style={{ flex: "none" }}>User Profile</span><div className="order-main"><div className="order-meta">Access within the shop they're assigned to, limited to granted permissions.</div></div></div>
      </div>

      <div className="card" data-testid="users-list">
        {users.map((u) => (
          <div className="order-row" key={u.id} data-testid={`user-row-${u.id}`}>
            <div className="avatar">{u.name[0]?.toUpperCase()}</div>
            <div className="order-main"><strong>{u.name}</strong><div className="order-meta">{u.role.replace("_", " ")} · {u.email}</div></div>
            <span className={`status ${u.is_active ? "active" : "inactive"}`}>{u.is_active ? "Active" : "Inactive"}</span>
            <button className={`btn ${u.is_active ? "btn-danger" : "btn-primary"} btn-sm`} style={{ marginLeft: 10 }} onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`}>
              {u.is_active ? "Deactivate" : "Reactivate"}
            </button>
          </div>
        ))}
        {users.length === 0 && <div className="empty" style={{ margin: 20 }}>No team members yet</div>}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add user profile"
        footer={<button className="btn btn-primary" onClick={save} data-testid="save-user-btn">Create user</button>}>
        <div><label className="field-label">Name</label><input className="input" style={{ width: "100%" }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
        <div><label className="field-label">Email</label><input type="email" className="input" style={{ width: "100%" }} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
        <div><label className="field-label">Password</label><input type="password" className="input" style={{ width: "100%" }} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
        {user.role === "admin" && (
          <>
            <div><label className="field-label">Role</label>
              <select className="select" style={{ width: "100%" }} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
                <option value="shop_admin">Shop Admin</option>
                <option value="staff">Staff (User Profile)</option>
              </select>
            </div>
            <div><label className="field-label">Shop</label>
              <select className="select" style={{ width: "100%" }} value={form.shop_id} onChange={(e) => setForm({ ...form, shop_id: e.target.value })} data-testid="user-shop-select">
                {shops.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            </div>
          </>
        )}
        {form.role === "staff" && (
          <div>
            <label className="field-label">Permissions</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {ALL_PERMISSIONS.map((p) => (
                <span
                  key={p} className="chip" style={{ cursor: "pointer", background: form.permissions.includes(p) ? "var(--orange)" : "var(--surface-2)", color: form.permissions.includes(p) ? "#fff" : "var(--fg-muted)", borderColor: form.permissions.includes(p) ? "var(--orange)" : "var(--border)" }}
                  onClick={() => togglePerm(p)} data-testid={`perm-chip-${p}`}
                >
                  {PERMISSION_LABELS[p]}
                </span>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
